import { useState, useEffect } from 'react';
import { useController } from 'react-hook-form';
import { Box, Typography, SearchField } from '../../ui/core';
import api from '../../lib/api';

// Last 4 of the Mongo user _id — used as a disambiguator when a person has no
// phone/email on file, and always in the muted second line of an option.
const last4 = (id) => (id ? `…${String(id).slice(-4)}` : '');

// A readable one-line label for the input display: "<Name> (<phone|email|…last4>)".
// Guards the freeSolo/uncontrolled cases where the Autocomplete may hand this a
// bare string or null instead of a user object.
export const personLabel = (user) => {
  if (!user || typeof user === 'string') return user || '';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unknown';
  const contact = user.phoneNumber || user.email || last4(user.id);
  return contact ? `${name} (${contact})` : name;
};

// Debounced name/phone/email typeahead that submits the picked person's Mongo
// user `_id` (NEVER driver_id) into the RHF field. `kind` selects the search
// source: 'user' → searchUsers, 'driver' → searchDrivers.
const PersonSearchField = ({ control, name, kind, placeholder }) => {
  const { field, fieldState } = useController({ name, control });

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  // Reset sync: when the RHF value is cleared externally (e.g. form.reset()
  // after a successful submit), drop the displayed selection + input text so the
  // picked person doesn't linger in the box.
  useEffect(() => {
    if (!field.value && selected) {
      setSelected(null);
      setInputValue('');
    }
  }, [field.value, selected]);

  // Debounced search (500ms). Empty query or a failed request → [], never throw.
  useEffect(() => {
    const q = inputValue.trim();
    if (!q) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    // `active` guards against out-of-order responses: a slow earlier request
    // must not overwrite the options of a newer query. The cleanup flips it
    // false so a superseded request's result is dropped.
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const results =
          kind === 'driver' ? await api.search.searchDrivers({ q }) : await api.search.searchUsers({ q });
        if (active) setOptions(results || []);
      } catch (err) {
        if (active) setOptions([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 500);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [inputValue, kind]);

  return (
    <Box>
      <SearchField
        sx={{ width: '100%' }}
        placeholder={placeholder}
        value={selected}
        inputValue={inputValue}
        options={options}
        loading={loading}
        getOptionLabel={personLabel}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        onInputChange={(event, newInputValue, reason) => {
          setInputValue(newInputValue);
          // MUI only commits a new value when an option is picked (or Enter in
          // freeSolo). So once a person is selected, editing the text would
          // leave the old id in the form while the box shows something else —
          // a silent wrong-pair submit. Typing over a committed selection
          // (reason 'input') invalidates it: drop the id until they re-pick.
          if (reason === 'input' && selected) {
            setSelected(null);
            field.onChange('');
          }
        }}
        onChange={(event, value) => {
          const user = value && typeof value === 'object' ? value : null;
          setSelected(user);
          // Submit the user `_id` for both rider and driver — the exclusion
          // registry keys on the user _id, never driver_id.
          field.onChange(user?.id || '');
          field.onBlur();
        }}
        renderOption={(props, user) => {
          const nameLine = [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unknown';
          const disambiguator = [user.phoneNumber || user.email, last4(user.id)].filter(Boolean).join(' · ');
          return (
            <Box component="li" key={user.id} {...props} sx={{ display: 'block !important' }}>
              <Typography component="div">{nameLine}</Typography>
              <Typography component="div" variant="body2" color="text.secondary">
                {disambiguator}
              </Typography>
            </Box>
          );
        }}
        blurOnSelect
      />
      {fieldState.error && (
        <Typography sx={{ fontSize: '12px', color: 'error.main', mt: '4px' }}>
          {fieldState.error.message}
        </Typography>
      )}
    </Box>
  );
};

export default PersonSearchField;
